import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type TSourceType = string;
type TSourceTypes = TSourceType[];

type TSentiment = string;
type TSentiments = TSentiment[];
type TLanguage = string;
type TLanguages = TLanguage[];

type TEmotion = string;
type TEmotions = TEmotion[];

type TAuthor = string;
type TAuthors = TAuthor[];

type TLocation = string;
type TLocations = TLocation[];

export interface IAdvanceSearchLandingOverview {
  sourceTypes: TSourceTypes;
  sentiments: TSentiments;
  languages: TLanguages;
  authors: TAuthors;
  locations: TLocations;
  excludeKeywords: string[];
  includeKeywords: string[];
  keywords: string[];
  mediaList: string[];
  mediaListOnm: string[];
  mediaListOfm: string[];
  emotion?: TEmotions;
  isModalOpen: boolean;
  // Token to force refresh queries even if filters are unchanged
  refreshSearchToken?: number;
  // Pending filters that are not yet applied
  pendingSourceTypes: TSourceTypes;
  pendingSentiments: TSentiments;
  pendingLanguages: TLanguages;
  pendingAuthors: TAuthors;
  pendingLocations: TLocations;
  pendingExcludeKeywords: string[];
  pendingIncludeKeywords: string[];
  pendingKeywords: string[];
  pendingMediaList: string[];
  pendingMediaListOnm: string[];
  pendingMediaListOfm: string[];
  pendingEmotion?: TEmotions;
}

export const initialState: IAdvanceSearchLandingOverview = {
  sourceTypes: [],
  sentiments: [],
  languages: [],
  authors: [],
  locations: [],
  excludeKeywords: [],
  includeKeywords: [],
  keywords: [],
  mediaList: [],
  emotion: [],
  mediaListOnm: [],
  mediaListOfm: [],
  isModalOpen: false,
  refreshSearchToken: 0,
  // Initialize pending filters as empty
  pendingSourceTypes: [],
  pendingSentiments: [],
  pendingLanguages: [],
  pendingAuthors: [],
  pendingLocations: [],
  pendingExcludeKeywords: [],
  pendingIncludeKeywords: [],
  pendingKeywords: [],
  pendingMediaList: [],
  pendingMediaListOnm: [],
  pendingMediaListOfm: [],
  pendingEmotion: []
};

export const advanceSearchLandingOverview = createSlice({
  name: 'advanceSearchLandingOverview',
  initialState,
  reducers: {
    reducerUpdateKeywords: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.keywords = action.payload;
    },
    reducerClearKeywords: (state: IAdvanceSearchLandingOverview) => {
      state.keywords = [];
    },
    reducerUpdateExcludeKeywords: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.excludeKeywords = action.payload;
    },
    reducerClearExcludeKeywords: (state: IAdvanceSearchLandingOverview) => {
      state.excludeKeywords = [];
    },
    reducerUpdateIncludeKeywords: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.includeKeywords = action.payload;
    },
    reducerClearIncludeKeywords: (state: IAdvanceSearchLandingOverview) => {
      state.includeKeywords = [];
    },
    reducerUpdateSourceTypes: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TSourceTypes>
    ) => {
      state.sourceTypes = action.payload;
    },
    reducerUpdateSentiments: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TSentiments>
    ) => {
      state.sentiments = action.payload;
    },
    reducerUpdateLanguages: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TLanguages>
    ) => {
      state.languages = action.payload;
    },
    reducerUpdateEmotion: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TEmotions>
    ) => {
      state.emotion = action.payload;
    },
    reducerUpdateAuthors: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TAuthors>
    ) => {
      state.authors = action.payload;
    },
    reducerUpdateLocations: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TLocations>
    ) => {
      state.locations = action.payload;
    },
    reducerUpdateMediaList: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.mediaList = action.payload;
    },
    reducerUpdateMediaListOnm: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.mediaListOnm = action.payload;
    },
    reducerUpdateMediaListOfm: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.mediaListOfm = action.payload;
    },
    // Pending filter reducers
    reducerUpdatePendingSourceTypes: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TSourceTypes>
    ) => {
      state.pendingSourceTypes = action.payload;
    },
    reducerUpdatePendingSentiments: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TSentiments>
    ) => {
      state.pendingSentiments = action.payload;
    },
    reducerUpdatePendingLanguages: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TLanguages>
    ) => {
      state.pendingLanguages = action.payload;
    },
    reducerUpdatePendingEmotion: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TEmotions>
    ) => {
      state.pendingEmotion = action.payload;
    },
    reducerUpdatePendingAuthors: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TAuthors>
    ) => {
      state.pendingAuthors = action.payload;
    },
    reducerUpdatePendingLocations: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<TLocations>
    ) => {
      state.pendingLocations = action.payload;
    },
    reducerUpdatePendingMediaList: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.pendingMediaList = action.payload;
    },
    reducerUpdatePendingMediaListOnm: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.pendingMediaListOnm = action.payload;
    },
    reducerUpdatePendingMediaListOfm: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.pendingMediaListOfm = action.payload;
    },
    reducerUpdatePendingKeywords: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.pendingKeywords = action.payload;
    },
    reducerUpdatePendingExcludeKeywords: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.pendingExcludeKeywords = action.payload;
    },
    reducerUpdatePendingIncludeKeywords: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<string[]>
    ) => {
      state.pendingIncludeKeywords = action.payload;
    },
    reducerOpenModal: (state: IAdvanceSearchLandingOverview) => {
      state.isModalOpen = true;
    },
    reducerCloseModal: (state: IAdvanceSearchLandingOverview) => {
      state.isModalOpen = false;
    },
    reducerBumpRefreshSearchToken: (state: IAdvanceSearchLandingOverview) => {
      state.refreshSearchToken = (state.refreshSearchToken || 0) + 1;
    },
    reducerClearRefreshSearchToken: (state: IAdvanceSearchLandingOverview) => {
      state.refreshSearchToken = 0;
    },
    reducerUpdateAdvanceSearchLandingOverview: (
      state: IAdvanceSearchLandingOverview,
      action: PayloadAction<IAdvanceSearchLandingOverview>
    ) => {
      state.sourceTypes = action.payload.sourceTypes;
      state.sentiments = action.payload.sentiments;
      state.languages = action.payload.languages;
      state.authors = action.payload.authors;
      state.locations = action.payload.locations;
      state.emotion = action.payload.emotion;
    },
    reducerClearAdvanceSearchLandingOverview: (
      state: IAdvanceSearchLandingOverview
    ) => {
      state.sourceTypes = [];
      state.sentiments = [];
      state.languages = [];
      state.authors = [];
      state.locations = [];
      state.excludeKeywords = [];
      state.includeKeywords = [];
      state.keywords = [];
      state.mediaList = [];
      state.emotion = [];
      state.mediaListOnm = [];
      state.mediaListOfm = [];
      // Also clear pending filters
      state.pendingSourceTypes = [];
      state.pendingSentiments = [];
      state.pendingLanguages = [];
      state.pendingAuthors = [];
      state.pendingLocations = [];
      state.pendingExcludeKeywords = [];
      state.pendingIncludeKeywords = [];
      state.pendingKeywords = [];
      state.pendingMediaList = [];
      state.pendingEmotion = [];
      state.pendingMediaListOnm = [];
      state.pendingMediaListOfm = [];
    },
    reducerApplyPendingFilters: (state: IAdvanceSearchLandingOverview) => {
      // Apply pending filters to active filters
      state.sourceTypes = state.pendingSourceTypes;
      state.sentiments = state.pendingSentiments;
      state.languages = state.pendingLanguages;
      state.authors = state.pendingAuthors;
      state.locations = state.pendingLocations;
      state.excludeKeywords = state.pendingExcludeKeywords;
      state.includeKeywords = state.pendingIncludeKeywords;
      state.keywords = state.pendingKeywords;
      state.mediaList = state.pendingMediaList;
      state.emotion = state.pendingEmotion;
      state.mediaListOnm = state.pendingMediaListOnm;
      state.mediaListOfm = state.pendingMediaListOfm;
    },
    reducerInitializePendingFilters: (state: IAdvanceSearchLandingOverview) => {
      // Initialize pending filters with current active filters when modal opens
      state.pendingSourceTypes = state.sourceTypes;
      state.pendingSentiments = state.sentiments;
      state.pendingLanguages = state.languages;
      state.pendingAuthors = state.authors;
      state.pendingLocations = state.locations;
      state.pendingExcludeKeywords = state.excludeKeywords;
      state.pendingIncludeKeywords = state.includeKeywords;
      state.pendingKeywords = state.keywords;
      state.pendingMediaList = state.mediaList;
      state.pendingEmotion = state.emotion || [];
      state.pendingMediaListOnm = state.mediaListOnm || [];
      state.pendingMediaListOfm = state.mediaListOfm || [];
    }
  }
});

export const {
  reducerClearKeywords,
  reducerClearIncludeKeywords,
  reducerClearExcludeKeywords,
  reducerUpdateKeywords,
  reducerUpdateExcludeKeywords,
  reducerUpdateIncludeKeywords,
  reducerUpdateSourceTypes,
  reducerUpdateSentiments,
  reducerUpdateLanguages,
  reducerUpdateEmotion,
  reducerUpdateAuthors,
  reducerUpdateLocations,
  reducerUpdateMediaList,
  reducerUpdateMediaListOnm,
  reducerUpdateMediaListOfm,
  reducerUpdatePendingSourceTypes,
  reducerUpdatePendingSentiments,
  reducerUpdatePendingLanguages,
  reducerUpdatePendingEmotion,
  reducerUpdatePendingAuthors,
  reducerUpdatePendingLocations,
  reducerUpdatePendingMediaList,
  reducerUpdatePendingMediaListOnm,
  reducerUpdatePendingMediaListOfm,
  reducerUpdatePendingKeywords,
  reducerUpdatePendingExcludeKeywords,
  reducerUpdatePendingIncludeKeywords,
  reducerOpenModal,
  reducerCloseModal,
  reducerBumpRefreshSearchToken,
  reducerClearRefreshSearchToken,
  reducerUpdateAdvanceSearchLandingOverview,
  reducerClearAdvanceSearchLandingOverview,
  reducerApplyPendingFilters,
  reducerInitializePendingFilters
} = advanceSearchLandingOverview.actions;

export default advanceSearchLandingOverview.reducer;
